import type { ProcessNodeDto } from '@api/processes';
import type { InputNodeData, ProcessNode } from '../graphTypes';

/**
 * Adapter: converts a live React-Flow {@link ProcessNode} (an Input node)
 * into the {@link ProcessNodeDto} shape that {@link RunConfigForm} consumes.
 *
 * <p>The form was originally written against the persisted `GET /processes/{id}`
 * detail payload, but the in-editor TestRunDialog needs to feed it from the
 * live graph so the user's most recent edits — particularly node renames —
 * show up immediately without waiting for the auto-save round-trip. This
 * adapter is the smallest viable bridge between the two shapes; it only
 * fills the fields the form actually reads (label, config.dataType,
 * config.required, config.text → hint, ports). Run-snapshot fields
 * (latestExecution / latestArtifacts) are nulled out because the dialog
 * doesn't render them.</p>
 */
export function liveInputNodeToDto(node: ProcessNode): ProcessNodeDto {
  // Defensive runtime guard: ProcessNode is a discriminated union over
  // ProcessNodeData; calling this with anything but an Input node would
  // silently return a DTO with the wrong fields read from the wrong
  // shape (e.g. dataType undefined, hint absent) and the form would
  // render a broken row. Fail loudly so the bug surfaces at the call
  // site, not three layers down inside RunConfigForm.
  if (node.type !== 'input') {
    throw new Error(
      `liveInputNodeToDto: expected node.type === 'input', got '${node.type ?? 'undefined'}'`,
    );
  }
  const data = node.data as InputNodeData;
  return {
    nodeId: node.id,
    definitionId: 'input',
    definitionVersion: 1,
    kind: 'input',
    label: data.label,
    instruction: null,
    config: {
      dataType: data.dataType,
      required: data.required,
      text: data.hint,
    },
    ports: data.ports ?? [],
    positionX: node.position.x,
    positionY: node.position.y,
    latestExecution: null,
    latestArtifacts: [],
  };
}
